import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { Panel, Btn, PageHead, StatusDot } from "@/components/kit";
import { Check, X, Lock, ShieldAlert, Play, RefreshCw, AlertTriangle } from "lucide-react";

export default function Approvals() {
    const qc = useQueryClient();
    const [preview, setPreview] = useState(null);
    const { data: approvals = [] } = useQuery({
        queryKey: ["approvals"],
        queryFn: async () => (await api.get("/approvals")).data,
        refetchInterval: 3000,
    });

    const approve = useMutation({
        mutationFn: async ({ id, force = false }) =>
            (await api.post(`/approvals/${id}/approve${force ? "?force=true" : ""}`)).data,
        onSuccess: () => {
            toast.success("Aprobado · subido en PRIVATE vía yutu");
            setPreview(null);
            qc.invalidateQueries({ queryKey: ["approvals"] });
            qc.invalidateQueries({ queryKey: ["stats"] });
        },
        onError: (e, vars) => {
            if (e?.response?.status === 409) {
                const msg = e.response.data?.detail || "Este vídeo no pasó el control de calidad.";
                if (window.confirm(`${msg}\n\n¿Publicarlo de todos modos?`)) {
                    approve.mutate({ id: vars.id, force: true });
                }
                return;
            }
            toast.error("Fallo aprobando");
        },
    });
    const reject = useMutation({
        mutationFn: async (id) => (await api.post(`/approvals/${id}/reject`)).data,
        onSuccess: () => {
            toast("Rechazado", { icon: "⚠" });
            setPreview(null);
            qc.invalidateQueries({ queryKey: ["approvals"] });
        },
    });
    const regenerate = useMutation({
        mutationFn: async (id) => (await api.post(`/approvals/${id}/regenerate`)).data,
        onSuccess: () => {
            toast.success("Rechazado y re-lanzado: nuevo corte en producción", { duration: 6000 });
            setPreview(null);
            qc.invalidateQueries({ queryKey: ["approvals"] });
            qc.invalidateQueries({ queryKey: ["jobs"] });
        },
        onError: (e) => toast.error(e?.response?.data?.detail || "Fallo regenerando"),
    });

    const pending = approvals.filter((a) => a.status === "pending");
    const done = approvals.filter((a) => a.status !== "pending");

    return (
        <div data-testid="approvals-page">
            <PageHead
                title="COLA DE APROBACIÓN"
                sub="Human-in-the-loop de 1 clic. Todo sube en private por defecto con disclosure de IA. Preguntar → Confirmar → Ejecutar."
            />

            <div className="mb-4 flex items-center gap-2 text-xs text-status-yellow">
                <ShieldAlert size={14} /> {pending.length} elemento(s) esperando aprobación manual
            </div>

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                {pending.map((a) => (
                    <div key={a.id} className="border border-border bg-card" data-testid={`approval-card-${a.id}`}>
                        <div className="relative">
                            <img src={a.thumbnail} alt="" className="h-48 w-full object-cover opacity-90" />
                            <div className="absolute left-3 top-3 flex items-center gap-2 border border-primary bg-black/70 px-2 py-1 text-[10px] uppercase text-primary">
                                <StatusDot color="#00FFFF" pulse /> Listo · {a.format}
                            </div>
                            <div className="absolute right-3 top-3 flex items-center gap-1 border border-border bg-black/70 px-2 py-1 text-[10px] uppercase text-muted-foreground">
                                <Lock size={10} /> private
                            </div>
                        </div>
                        <div className="p-5">
                            <div className="mb-1 flex items-center justify-between">
                                <h3 className="font-head text-base">{a.title}</h3>
                                <span className="text-xs text-primary">{a.channel_name}</span>
                            </div>
                            <p className="mb-3 max-h-24 overflow-y-auto text-xs text-muted-foreground">
                                {a.script}
                            </p>
                            <div className="mb-4 border border-border p-3">
                                <div className="label mb-1">SEO · {a.seo?.title}</div>
                                <div className="flex flex-wrap gap-1">
                                    {(a.seo?.tags || []).map((t, i) => (
                                        <span key={i} className="border border-border px-1.5 py-0.5 text-[9px] text-muted-foreground">
                                            {t}
                                        </span>
                                    ))}
                                </div>
                                <div className="mt-2 text-[10px] text-status-green">✓ Disclosure de IA incluido</div>
                            </div>
                            {a.qc_failed && (
                                <div
                                    className="mb-3 flex items-start gap-2 border p-2 text-[10px]"
                                    style={{ color: "#FFD700", borderColor: "#FFD700" }}
                                    data-testid={`card-qc-failed-${a.id}`}
                                >
                                    <ShieldAlert size={12} className="mt-0.5 shrink-0" />
                                    <span>No pasó el control de calidad: {((a.qc?.reasons) || []).join("; ") || "revisar"}. Requiere confirmación para publicar.</span>
                                </div>
                            )}
                            {!a.video_url && a.render_error && (
                                <div
                                    className="mb-3 flex items-start gap-2 border p-2 text-[10px]"
                                    style={{ color: "#FF6B6B", borderColor: "#FF6B6B" }}
                                    data-testid={`card-render-error-${a.id}`}
                                >
                                    <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                                    <span>Sin MP4 — {a.render_error}</span>
                                </div>
                            )}
                            <div className="flex gap-3">
                                <Btn
                                    testId={`preview-btn-${a.id}`}
                                    variant="ghost"
                                    onClick={() => setPreview(a)}
                                >
                                    <span className="flex items-center gap-2">
                                        <Play size={14} /> Ver video
                                    </span>
                                </Btn>
                                <Btn
                                    testId={`approve-btn-${a.id}`}
                                    variant="green"
                                    className="flex-1"
                                    onClick={() => approve.mutate({ id: a.id })}
                                    disabled={approve.isPending}
                                >
                                    <span className="flex items-center justify-center gap-2">
                                        <Check size={14} /> {a.qc_failed ? "Publicar igual" : "Aprobar y subir"}
                                    </span>
                                </Btn>
                                <Btn
                                    testId={`reject-btn-${a.id}`}
                                    variant="danger"
                                    onClick={() => reject.mutate(a.id)}
                                >
                                    <X size={14} />
                                </Btn>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {!pending.length && (
                <Panel title="Sin pendientes" className="mb-6">
                    <p className="text-sm text-muted-foreground">
                        No hay videos esperando aprobación. Lanza producción para generar nuevos.
                    </p>
                </Panel>
            )}

            {done.length > 0 && (
                <Panel title="Historial reciente" className="mt-6" testId="approval-history">
                    <ul className="space-y-2 text-sm">
                        {done.slice(0, 10).map((a) => (
                            <li key={a.id} className="flex items-center justify-between border-b border-border/40 pb-2">
                                <span>{a.title}</span>
                                <span
                                    className="text-xs uppercase"
                                    style={{
                                        color:
                                            a.status === "approved"
                                                ? "#39FF14"
                                                : a.status === "regenerating"
                                                ? "#FFD700"
                                                : "#FF003C",
                                    }}
                                >
                                    {a.status}
                                </span>
                            </li>
                        ))}
                    </ul>
                </Panel>
            )}

            {/* -------- video preview modal -------- */}
            {preview && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4"
                    data-testid="video-preview-modal"
                    onClick={() => setPreview(null)}
                >
                    <div
                        className="max-h-[92vh] w-full max-w-2xl overflow-y-auto border border-primary bg-card"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between border-b border-border px-4 py-3">
                            <span className="label">Previsualización · {preview.format}</span>
                            <button
                                data-testid="preview-close-btn"
                                onClick={() => setPreview(null)}
                                className="text-muted-foreground hover:text-foreground"
                            >
                                <X size={16} />
                            </button>
                        </div>
                        <div className="p-5">
                            <h3 className="font-head text-lg">{preview.title}</h3>
                            <div className="mb-3 text-xs text-primary">{preview.channel_name}</div>

                            {preview.video_url ? (
                                <video
                                    data-testid="preview-video-player"
                                    controls
                                    preload="metadata"
                                    className="w-full border border-border bg-black"
                                    src={preview.video_url}
                                />
                            ) : (
                                <div>
                                    <img
                                        src={preview.thumbnail}
                                        alt=""
                                        className="h-56 w-full border border-border object-cover opacity-90"
                                    />
                                    <div
                                        className="mt-2 flex items-start gap-2 border p-2 text-[11px]"
                                        style={{ color: "#FFD700", borderColor: "#FFD700" }}
                                        data-testid="preview-no-video-note"
                                    >
                                        <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                                        <span>
                                            Este corte no tiene MP4: el render cinematográfico falló.
                                            {preview.render_error && (
                                                <span className="mt-1 block" style={{ color: "#FF6B6B" }} data-testid="preview-render-error">
                                                    Motivo: {preview.render_error}
                                                </span>
                                            )}
                                            Corrige el motivo y pulsa &quot;Rechazar y regenerar&quot; para
                                            producir el video con clips REALES (Veo/Sora-2).
                                        </span>
                                    </div>
                                </div>
                            )}

                            {preview.hook && (
                                <div className="mt-4">
                                    <div className="label mb-1">Gancho</div>
                                    <p className="text-sm text-primary">{preview.hook}</p>
                                </div>
                            )}
                            <div className="mt-3">
                                <div className="label mb-1">Guion completo</div>
                                <p className="max-h-48 overflow-y-auto whitespace-pre-wrap border border-border p-3 text-xs text-muted-foreground">
                                    {preview.script}
                                </p>
                            </div>
                            <div className="mt-3 border border-border p-3">
                                <div className="label mb-1">SEO · {preview.seo?.title}</div>
                                <p className="text-[11px] text-muted-foreground">{preview.seo?.description}</p>
                            </div>

                            <div className="mt-5 flex flex-wrap gap-3">
                                <Btn
                                    testId="preview-approve-btn"
                                    variant="green"
                                    className="flex-1"
                                    onClick={() => approve.mutate({ id: preview.id })}
                                    disabled={approve.isPending}
                                >
                                    <span className="flex items-center justify-center gap-2">
                                        <Check size={14} /> {preview.qc_failed ? "Publicar igual" : "Aprobar y subir"}
                                    </span>
                                </Btn>
                                <Btn
                                    testId="preview-regenerate-btn"
                                    onClick={() => regenerate.mutate(preview.id)}
                                    disabled={regenerate.isPending}
                                >
                                    <span className="flex items-center gap-2">
                                        <RefreshCw size={13} className={regenerate.isPending ? "animate-spin" : ""} />
                                        {regenerate.isPending ? "Re-lanzando…" : "Rechazar y regenerar"}
                                    </span>
                                </Btn>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
